import {useCallback, useState, useContext} from 'react';
import classNames from 'classnames';
import {useIntl} from 'react-intl';
import kuPiper from '@/middleware/kuPiper';

import * as store from '@/store';
import {InheritRepo, PageScope} from '@/Constant';
import {DOC_TYPE} from '@/constants/document/doc';
import {WizDocument, DocumentOperateType} from '@/interface';
import eventBus, {REMOVE_COMMON_DOC_SUCCESS} from '@/eventBus';
import {MoreSvg} from '@/icons';
import Anchor, {OpenType} from '@/components/Anchor';
import Icon from '@/components/Icon';
import DropMenu, {DropMenuSize, DropMenuTrigger, DropMenuValue} from '@/components/DropMenu';
import NodeIcon from '@/domains/space/Repository/SidebarNew/components/NodeIcon';

import {docRename} from '../../sidebarTreeUtils';
import Input from './Input';
import {DragContext} from '../Drag/Context';
import {removeCommonDoc} from '../utils';
import {DEFAULT_TITLE, DOC_TYPE_DEFAULT_TITLE} from '@/constants/document/title';

import commonStyles from '../../common.module.scss';
import styles from './index.module.scss';

function stopEvent(e: any) {
    e.stopPropagation();
    e.preventDefault();
}

enum MenuType {
    Rename = 'rename',
    RemoveCommonDoc = 'removeCommonDoc',
}

interface IProps {
    title: string;
    url: string;
    isSelected?: boolean;
    docData: WizDocument;
    setRenameMode: (val: boolean) => void;
    docsInMeeting?: string[];
}

const Node: React.FC<IProps> = ({
    title,
    url = '',
    isSelected = false,
    docData,
    setRenameMode,
    docsInMeeting = [],
}) => {
    const intl = useIntl();
    const docGuid = docData.docGuid;
    const isInMeeting = docsInMeeting.includes(docGuid);
    const isPagePublic = [PageScope.PagePublic, PageScope.PagePublicEdit].includes(docData.scope!);
    const isBook = docData.type === DOC_TYPE.DIRECTORY;
    const isSheet = docData.type === DOC_TYPE.SHEET;
    const isPPT = docData.type === DOC_TYPE.PPT;
    const isDataSheet = docData.type === DOC_TYPE.DATASHEET;
    const isNotInheritRepo = isBook && docData.inheritRepo === InheritRepo.Not_Inherit;

    const {dragMode} = useContext(DragContext);
    const [renaming, setRenamingState] = useState(false);
    const [dropMenuVisible, setDropMenuVisible] = useState(false);

    const setRenaming = useCallback(
        (val: boolean) => {
            setRenamingState(val);
            setRenameMode(val);
        },
        [setRenameMode]
    );

    const handleRename = useCallback(
        (val: string) => {
            setRenaming(false);
            const resVal = val.trim() || DOC_TYPE_DEFAULT_TITLE[docData.type as number] || DEFAULT_TITLE;
            if (resVal !== title) {
                docRename(docGuid, resVal).catch(() => {});
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [docGuid, setRenaming, title]
    );

    function handleClickMore(e: any, value: DropMenuValue) {
        stopEvent(e);
        if (value === MenuType.RemoveCommonDoc) {
            removeCommonDoc(docGuid).then(() => {
                eventBus.emit(REMOVE_COMMON_DOC_SUCCESS);

                store.setDocumentOperate({
                    docList: [
                        {
                            docGuid: docGuid,
                            active: false,
                        },
                    ],
                    type: DocumentOperateType.Update,
                    updateType: DocumentOperateType.Active,
                });
            });
        } else if (value === MenuType.Rename) {
            setRenaming(true);
        }
        setDropMenuVisible(false);
    }

    function onDropMenuPopperVisible(visible: boolean) {
        setDropMenuVisible(visible);
    }

    const moreOptions = [
        {
            label: <div onClick={(e) => handleClickMore(e, MenuType.Rename)}>
                {intl.formatMessage({id: 'indexMenuRename'})}
            </div>,
            value: MenuType.Rename,
        },
        {
            label: <div onClick={(e) => handleClickMore(e, MenuType.RemoveCommonDoc)}>
                {intl.formatMessage({id: 'removeCommonDoc'})}
            </div>,
            value: MenuType.RemoveCommonDoc,
        },
    ];

    const handleClickProxy = kuPiper.runProxy(kuPiper.proxyNameMap.treeCommonDocClickJump);

    function handleClick(e?: React.MouseEvent) {
        handleClickProxy(url, e);
    }

    return (
        <Anchor
            url={url}
            openType={OpenType.comAndClickOpenNewTab}
            onClick={handleClick}
        >
            <div className={classNames(styles.node, {[styles.selected]: isSelected, [styles.dragMode]: dragMode})}>
                <div className={styles.icon}>
                    <NodeIcon
                        isInMeeting={isInMeeting}
                        isPagePublic={isPagePublic}
                        isInTeam={isBook}
                        isSheet={isSheet}
                        isPPT={isPPT}
                        isDataSheet={isDataSheet}
                        isNotInheritRepo={isNotInheritRepo}
                        isSelected={isSelected}
                    />
                </div>
                {renaming ? (
                    <div onClick={stopEvent}>
                        <Input text={title} onBlur={handleRename} />
                    </div>
                ) : (
                    <>
                        <div className={styles.title}>{title}</div>
                        <DropMenu
                            triggerType={DropMenuTrigger.Click}
                            placement={'bottom-end'}
                            options={moreOptions}
                            value={null}
                            className={commonStyles.dropMenu}
                            // onChange={handleClickMore}
                            size={DropMenuSize.SM}
                            onChangeMenuPopperVisible={onDropMenuPopperVisible}
                        >
                            <Icon
                                onClick={stopEvent}
                                className={classNames(
                                    styles.moreIcon,
                                    styles.moreBtn,
                                    {[styles.hold]: dropMenuVisible}
                                )}
                                svg={MoreSvg}
                                noSpace
                            />
                        </DropMenu>
                    </>
                )}
            </div>
        </Anchor>
    );
};

const NodeComp = store.connect([store.KEYS.DOCS_IN_MEETING])(Node);

export default NodeComp;
